import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShoppingCart, AlertTriangle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import WhatsAppButton from "@/components/WhatsAppButton";

export default function ShoppingListCard({ apiCall }) {
  const [data, setData] = useState({ groups: [], total_items: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiCall("get", "/shopping-list");
      setData(res.data || { groups: [], total_items: 0 });
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <Card className="liquid-card p-5 rounded-2xl">
        <div className="text-sm text-muted-foreground">Carregando lista de compras...</div>
      </Card>
    );
  }

  if (data.total_items === 0) {
    return (
      <Card className="liquid-card p-5 rounded-2xl" data-testid="shopping-list-card">
        <div className="flex items-center gap-2 mb-2">
          <ShoppingCart className="h-4 w-4" strokeWidth={1.5} style={{ color: "#0EA5E9" }} />
          <div className="text-xs uppercase tracking-widest text-foreground/60 font-heading font-medium">Lista de Compras</div>
        </div>
        <div className="text-sm text-muted-foreground py-4 text-center">
          Estoque saudável ✨ Nenhum produto atingiu o mínimo.
        </div>
        <p className="text-[10px] text-muted-foreground text-center">
          Cadastre <strong>Estoque mínimo</strong> em cada produto para receber alertas automáticos.
        </p>
      </Card>
    );
  }

  const buildOrderMessage = (items, supplierName) => {
    const lines = items.map((it) => `• ${it.name}  ·  sugestão: ${it.suggested_quantity} un (atual: ${it.stock_quantity})`);
    return `Olá${supplierName ? ` ${supplierName}` : ""}, tudo bem? Preciso fazer um pedido para o salão Gregory João:\n\n${lines.join("\n")}\n\nObrigada!`;
  };

  return (
    <Card className="liquid-card p-5 rounded-2xl" data-testid="shopping-list-card">
      <div className="flex items-center gap-2 mb-4">
        <div className="rounded-full bg-amber-100 p-1.5">
          <ShoppingCart className="h-4 w-4" strokeWidth={1.5} style={{ color: "#D97706" }} />
        </div>
        <div className="text-xs uppercase tracking-widest text-foreground/60 font-heading font-medium">Lista de Compras</div>
        <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">
          {data.total_items} {data.total_items === 1 ? "item" : "itens"}
        </span>
        <Link to="/produtos" className="ml-auto text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
          Produtos <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
        </Link>
      </div>

      <div className="space-y-4">
        {data.groups.map((g, gi) => (
          <div key={gi} className="border border-amber-200/50 rounded-xl p-3 bg-white/30 backdrop-blur-sm" data-testid={`shopping-group-${gi}`}>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="text-sm font-medium">
                {g.supplier ? (
                  <>
                    🏷️ {g.supplier.name}
                    {g.supplier.contact_name && <span className="text-muted-foreground ml-2">({g.supplier.contact_name})</span>}
                  </>
                ) : (
                  <span className="text-muted-foreground">Sem fornecedor vinculado</span>
                )}
              </div>
              {g.supplier?.phone && (
                <WhatsAppButton
                  toPhone={g.supplier.phone}
                  message={buildOrderMessage(g.items, g.supplier.contact_name || g.supplier.name)}
                  variant="default"
                  size="sm"
                  buttonLabel={`Enviar pedido`}
                  iconOnly={false}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  testId={`shopping-order-${gi}`}
                />
              )}
            </div>
            <div className="space-y-1.5">
              {g.items.map((it) => (
                <div key={it.product_id} className="flex items-center gap-2 text-sm py-1 border-b border-amber-100/40 last:border-0">
                  <AlertTriangle className="h-3 w-3 text-amber-600 flex-shrink-0" strokeWidth={1.5} />
                  <span className="font-medium flex-1 truncate">{it.name}</span>
                  <span className="text-xs text-muted-foreground">atual: {it.stock_quantity}</span>
                  <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">repor {it.suggested_quantity}</span>
                </div>
              ))}
            </div>
            {!g.supplier && (
              <p className="text-[10px] text-muted-foreground mt-2">
                Edite os produtos acima e vincule um fornecedor para enviar pedido por WhatsApp.
              </p>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
